import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowsClockwise,
    CaretDown,
    Check,
    Cube,
    CurrencyCircleDollar,
    Lightning,
    ListBullets,
    MapPin,
    Plug,
    Star,
    Sun,
    X,
} from '@phosphor-icons/react';
import { Modal, notification, Tooltip } from 'antd';
import GenerateQuoteModal, { QuoteFormState } from './GenerateQuoteModal';
import { SolarQuoteFormState } from './SolarQuoteDetails';
import DashboardModal from './DashboardModal';
import './oneButton-css/EditorDashboardShell.css';
import './oneButton-css/FinancingSystemHeader.css';
import { FinancingCombinedView, FinancingDealHealthChip } from './FinancingCombinedView';
import { buildRecommendedCombinedFinancing } from './financingCombinedSummary';
import { useSunnyFocusedSectionOpen } from './useSunnyFocusedSectionOpen';

import {
    activeOpportunityProductTypeNames,
    pricingPayloadRequiresSolarMetrics,
} from '../../../../helpers/financeSelectors';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { getRoofSquaresFromSystemDesign } from '../../../../services/oneButtonServices/designServices';
import { effectiveAnnualUsageKwhForPricing } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import {
    annualProductionKwhFromSnapshot,
    isFinalizedProductsSolarPricingDesign,
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    systemSizeKwFromSnapshot,
} from '../../../../helpers/systemDesignSolarMetrics';
import { useViewerStore } from '../../../../store/store';
import {
    useOneButtonStore,
    COMMON_CONSTANTS,
    type FinancePartnerRow,
    type FinancingProduct,
    type PriceCell,
    type SelectedDesignForPricing,
    type PartnerSyncChecklist,
} from '../../../../store/oneButtonStore';
import { resolveUtilityCompanyCode } from './utils/pricing';

/** Grid columns: product types in this order */
const FINANCING_PRODUCTS: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Solar + Roof',
    'Battery',
    'Battery + Roof',
    'Solar + Battery',
];

/** Default partner order (must match {@link normalizePartner} output for merge to work). */
const DEFAULT_FINANCE_PARTNERS = ['Cash', 'GoodLeap', 'IGS', 'LightReach'];

/** Human-readable labels for the financing grid / view-all table only. */
const PARTNER_GRID_LABELS: Record<string, string> = {
    Cash: 'Cash',
    GoodLeap: 'Good Leap',
    IGS: 'IGS',
    LightReach: 'Lightreach',
};

/** Split "6,665 kWh (finalized design)" into value + short source badge. */
function splitFinancingProductionLabel(raw: string): { value: string; note: string | null } {
    const text = String(raw ?? '—').trim();
    const match = text.match(/^(.+?)\s*(\([^)]+\))\s*$/);
    if (!match) return { value: text || '—', note: null };
    const note = match[2].slice(1, -1).trim();
    return { value: match[1].trim() || '—', note: note || null };
}

function formatFinancingProductionNote(note: string): string {
    if (!note) return note;
    return note.charAt(0).toUpperCase() + note.slice(1);
}

/** Shown in grid cells when a partner has no pricing for a product column. */
const FINANCING_CELL_NA_LABEL = 'N/A';

function partnerLabelForGrid(partner: string): string {
    return PARTNER_GRID_LABELS[partner] ?? partner;
}

const TOOLTIP_ESCALATOR = 'Your monthly payment may increase by this percentage each year.';
const TOOLTIP_PROD_GUARANTEE = 'Lease includes a guarantee on system production.';

const FINANCE_METHOD_LABELS: Record<string, string> = {
    LOAN: 'Loan',
    LEASE: 'Lease',
    PPA: 'PPA',
    LEASEPRODGUAR: 'Lease (Prod Guarantee)',
};

function financeMethodToLabel(method: string): string {
    const u = (method || '').toUpperCase();
    return FINANCE_METHOD_LABELS[u] || method;
}

/** Tie-break for same monthly/total: PPA > LEASE > LOAN > other (Cash = 0). */
function financeMethodTierFromLabel(display: string | undefined, isCash: boolean): number {
    if (isCash) return 0;
    const s = (display || '').toLowerCase();
    if (s.includes('ppa')) return 3;
    if (s.includes('lease')) return 2;
    if (s.includes('loan')) return 1;
    return 0;
}

function partnerRowOrderIndex(partner: string): number {
    const i = DEFAULT_FINANCE_PARTNERS.indexOf(partner);
    return i === -1 ? 999 : i;
}

/**
 * Main grid: at most one "recommended" cell per product column — lowest payment first,
 * then PPA > LEASE > LOAN. Cash uses total (stored in cell.monthly); compared numerically
 * alongside financed monthly so the cheapest option wins.
 */
function applySingleRecommendedPerProductColumn(rows: FinancePartnerRow[]): void {
    for (const product of FINANCING_PRODUCTS) {
        type Cand = { rowIndex: number; sortKey: number; tier: number; partner: string };
        const candidates: Cand[] = [];
        rows.forEach((row, rowIndex) => {
            const cell = row.prices[product];
            if (!cell) return;
            const isCash = row.partner === 'Cash';
            const sortKey = cell.monthly;
            if (!Number.isFinite(sortKey) || sortKey <= 0) return;
            const tier = financeMethodTierFromLabel(cell.financeMethod, isCash);
            candidates.push({ rowIndex, sortKey, tier, partner: row.partner });
        });
        rows.forEach((row) => {
            const c = row.prices[product];
            if (c) c.recommended = false;
        });
        if (candidates.length === 0) continue;
        const minKey = Math.min(...candidates.map((c) => c.sortKey));
        const byMonth = candidates.filter((c) => c.sortKey === minKey);
        const maxTier = Math.max(...byMonth.map((c) => c.tier));
        const finalTie = byMonth.filter((c) => c.tier === maxTier);
        finalTie.sort((a, b) => partnerRowOrderIndex(a.partner) - partnerRowOrderIndex(b.partner));
        const win = finalTie[0];
        const cell = rows[win.rowIndex].prices[product];
        if (cell) cell.recommended = true;
    }
}

/** View-all modal: same single-winner rule per product (uses monthly or Cash loan/total). */
function applySingleRecommendedViewAllRows(rows: PricingViewAllRow[]): void {
    const byProduct = new Map<FinancingProduct, PricingViewAllRow[]>();
    for (const r of rows) {
        const pk = normalizeProductType(r.product);
        if (!pk || !FINANCING_PRODUCTS.includes(pk)) continue;
        const list = byProduct.get(pk) ?? [];
        list.push(r);
        byProduct.set(pk, list);
    }
    for (const product of FINANCING_PRODUCTS) {
        const group = byProduct.get(product);
        if (!group?.length) continue;
        group.forEach((r) => {
            r.recommended = false;
        });
        type Cand = { row: PricingViewAllRow; amt: number; tier: number; partner: string };
        const candidates: Cand[] = [];
        for (const r of group) {
            if (r.error) continue;
            const isCash = r.partner === 'Cash';
            const amt = isCash ? (r.loanAmount ?? r.monthly ?? 0) : (r.monthly ?? 0);
            if (!Number.isFinite(amt) || amt <= 0) continue;
            candidates.push({
                row: r,
                amt,
                tier: financeMethodTierFromLabel(r.financeMethod, isCash),
                partner: r.partner,
            });
        }
        if (candidates.length === 0) continue;
        const minAmt = Math.min(...candidates.map((c) => c.amt));
        const tied = candidates.filter((c) => c.amt === minAmt);
        const maxTier = Math.max(...tied.map((c) => c.tier));
        const tierTied = tied.filter((c) => c.tier === maxTier);
        tierTied.sort((a, b) => partnerRowOrderIndex(a.partner) - partnerRowOrderIndex(b.partner));
        tierTied[0].row.recommended = true;
    }
}

/** Normalize API ProductType to grid product key. Solar + Battery + Roof is not shown as a column. */
function normalizeProductType(productType: string): FinancingProduct | null {
    const t = (productType || '').trim();
    if (!t) return null;
    const lower = t.toLowerCase().replace(/\s+/g, '');
    // Exact compound matches (Solar + Battery + Roof excluded from grid — return null)
    if (lower === 'solarbatteryroof' || lower === 'solar+battery+roof') return null;
    if (lower === 'solarbattery') return 'Solar + Battery';
    if (lower === 'solarroof') return 'Solar + Roof';
    if (lower === 'batteryroof') return 'Battery + Roof';
    if (lower === 'solar+battery') return 'Solar + Battery';
    if (lower === 'solar+roof') return 'Solar + Roof';
    if (lower === 'battery+roof') return 'Battery + Roof';
    // Single product types
    if (lower === 'solar') return 'Solar';
    if (lower === 'roof') return 'Roof';
    if (lower === 'battery') return 'Battery';
    if (FINANCING_PRODUCTS.includes(t as FinancingProduct)) return t as FinancingProduct;
    return null;
}

/** New API: array of { FinancePartner, pricing: [{ ProductType, FinanceMethod, Offer: [{ payload?, __error?, Recommended }] }] } */
export interface PricingPartnerBlock {
    FinancePartner: string;
    pricing: Array<{
        ProductType?: string;
        FinanceMethod?: string;
        financeMethodName?: string;
        Offer?: Array<{ payload?: any; __error?: string; Recommended?: boolean;[k: string]: any }>;
    }>;
}

/** Normalize partner name for display (LightReach, GoodLeap, IGS, Cash) */
function normalizePartner(partner: string): string {
    const lower = (partner || '').toLowerCase();
    if (lower.includes('good') && lower.includes('leap')) return 'GoodLeap';
    if (lower.includes('light') && lower.includes('reach')) return 'LightReach';
    if (lower === 'igs' || lower === 'ics') return 'IGS';
    if (lower.includes('cash')) return 'Cash';
    return partner || 'Unknown';
}

/** Extract monthly from a single offer (GoodLeap, IGS, LightReach). Prefer totalMonthlyPayment/monthlyPayment; do not use batteryPayment. */
function monthlyFromOffer(offer: any, partnerNorm: string): number {
    if (offer?.__error) return 0;
    const payload = offer?.payload;

    // GoodLeap Loan: payload is array with calculations[]
    if (Array.isArray(payload) && payload[0]) {
        const calc = payload[0]?.calculations?.[0] ?? payload[0]?.Calculations?.[0];
        const v =
            calc?.roundedAmount?.value ??
            calc?.RoundedAmount?.Value ??
            calc?.amount?.value ??
            calc?.Amount?.Value;
        if (typeof v === 'number') return v;
    }

    // GoodLeap Lease / PPA: payload is object with pricing[0].firstYearMonthlyPayment.value
    if (payload && !Array.isArray(payload)) {
        const firstPricing = payload.pricing?.[0] ?? payload.Pricing?.[0];
        const firstYear =
            firstPricing?.firstYearMonthlyPayment?.value ??
            firstPricing?.FirstYearMonthlyPayment?.Value;
        if (typeof firstYear === 'number') return firstYear;

        // LightReach PPA: use totalMonthlyPayment or monthlyPayment (never batteryPayment)
        // Case 1: payload.monthlyPayments[0] has totalMonthlyPayment / monthlyPayment
        const firstMp = payload.monthlyPayments?.[0] ?? payload.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;

        // Case 2: payload itself is the first payment object (e.g. ROOF product)
        const directVal =
            payload?.totalMonthlyPayment ??
            payload?.TotalMonthlyPayment ??
            payload?.monthlyPayment ??
            payload?.MonthlyPayment;
        if (typeof directVal === 'number') return directVal;
    }

    // LightReach: monthlyPayments can be on the offer itself
    if (partnerNorm.includes('lightreach')) {
        const firstMp = offer?.monthlyPayments?.[0] ?? offer?.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;
    }

    // IGS Lease (and any flat offers): estimatedMonthlyPayment on offer
    const est = offer?.estimatedMonthlyPayment ?? offer?.EstimatedMonthlyPayment;
    if (typeof est === 'number') return est;
    return 0;
}

/** Extract term (years) and APR (%) from an ExcelOffer */
function termAprFromOffer(offer: any): { term: number; apr: number } {
    let term: number = offer?.ExcelOffer?.term ?? offer?.ExcelOffer?.Term ?? 0;
    let apr: number = offer?.ExcelOffer?.apr ?? offer?.ExcelOffer?.Apr ?? 0;

    // Normalise APR: backend may send 0.0949 (fraction) or 9.49 (percent)
    if (apr > 0 && apr < 1) apr = +(apr * 100).toFixed(2);

    return { term, apr };
}

/** Extract loan amount or total cost for display (CASH offers use totalCost) */
function loanAmountFromOffer(offer: any): number | null {
    if (offer?.__error) return null;
    const payload = offer?.payload;
    if (Array.isArray(payload) && payload[0]) {
        const la = payload[0]?.loanAmount ?? payload[0]?.LoanAmount;
        const v = la?.value ?? la?.Value;
        if (typeof v === 'number') return v;
    }
    if (typeof offer?.totalCost === 'number') return offer.totalCost;
    return null;
}

/** Solar-only EPC / purchase component on PPA & lease offers (several API spellings). */
function solarOnlyEpcPaymentFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;
    const read = (o: any): unknown =>
        o?.solarOnlyEpcPayment ?? o?.SolarOnlyEpcPayment ?? o?.solar_only_epc_payment;
    let v: unknown = read(offer);
    if (v == null || v === '') v = read(offer?.ExcelOffer);
    if ((v == null || v === '') && offer?.payload != null) {
        const p = offer.payload;
        if (Array.isArray(p) && p[0]) v = read(p[0]) ?? read(p[0]?.ExcelOffer);
        else if (typeof p === 'object') v = read(p) ?? read((p as any).ExcelOffer);
    }
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v.replace(/[$,]/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/** GoodLeap PPA: payload.pricing[].purchasePrice.value when API does not set solarOnlyEpcPayment. */
function goodLeapPayloadPricingPurchasePriceFromOffer(offer: any): number | null {
    if (!offer || offer.__error) return null;

    const readFromLine = (line: any): number | null => {
        if (!line || typeof line !== 'object') return null;
        const pp = line.purchasePrice ?? line.PurchasePrice;
        const v = pp?.value ?? pp?.Value;
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
        if (typeof v === 'string' && v.trim() !== '') {
            const n = parseFloat(v.replace(/[$,]/g, ''));
            return Number.isFinite(n) && n > 0 ? n : null;
        }
        return null;
    };

    const pricingFrom = (row: any): any[] | null => {
        if (!row || typeof row !== 'object') return null;
        const pr = row.pricing ?? row.Pricing;
        return Array.isArray(pr) ? pr : null;
    };

    const payload = offer.payload;
    if (Array.isArray(payload)) {
        for (const row of payload) {
            const pr = pricingFrom(row);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    } else if (payload && typeof payload === 'object') {
        const top = pricingFrom(payload);
        if (top) {
            for (const line of top) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
        for (const val of Object.values(payload)) {
            if (!val || typeof val !== 'object') continue;
            const pr = pricingFrom(val);
            if (!pr) continue;
            for (const line of pr) {
                const n = readFromLine(line);
                if (n != null) return n;
            }
        }
    }
    return null;
}

/** Single “purchase” figure for GoodLeap UI: loan, EPC, or cash total depending on finance method. */
function goodLeapPurchasePriceFromOffer(offer: any, financeMethodRaw: string): number | null {
    if (!offer || offer.__error) return null;
    const m = (financeMethodRaw || '').toUpperCase().replace(/\s+/g, '');
    if (m === 'CASH') {
        if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
            return offer.totalCost;
        }
        return null;
    }
    if (m === 'LOAN') {
        return loanAmountFromOffer(offer);
    }
    if (m === 'PPA' || m === 'LEASE' || m.startsWith('LEASE')) {
        const purchase = goodLeapPayloadPricingPurchasePriceFromOffer(offer);
        if (purchase != null && purchase > 0) return purchase;
        const epc = solarOnlyEpcPaymentFromOffer(offer);
        if (epc != null && epc > 0) return epc;
        const loan = loanAmountFromOffer(offer);
        return loan != null && loan > 0 ? loan : null;
    }
    const loan = loanAmountFromOffer(offer);
    if (loan != null && loan > 0) return loan;
    const epc = solarOnlyEpcPaymentFromOffer(offer);
    if (epc != null && epc > 0) return epc;
    if (typeof offer.totalCost === 'number' && Number.isFinite(offer.totalCost) && offer.totalCost > 0) {
        return offer.totalCost;
    }
    return null;
}

/** Escalator % from offer (for PPA/lease) */
function escalatorFromOffer(offer: any): number | null {
    const raw = offer?.ExcelOffer?.escalator ?? offer?.ExcelOffer?.Escalator;
    if (typeof raw !== 'number') return null;
    return raw > 0 && raw < 1 ? +(raw * 100).toFixed(1) : raw;
}

/** Tree cut cost ($) included on some pricing offers (tree-cutting scenarios). */
function treeCutCostFromOffer(offer: any): number | null {
    const raw = offer?.TreeCutCost ?? null;
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n >= 0 ? n : null;
}

export interface PricingViewAllRow {
    partner: string;
    product: string;
    financeMethod: string;
    treeDetailsText?: string;
    treeCutCost: number | null;
    monthly: number | null;
    loanAmount: number | null;
    term: number | null;
    apr: number | null;
    escalatorPct: number | null;
    minimumRate: number | null;
    rateCap: number | null;
    recommended: boolean;
    error: string | null;
}

export interface selectedQuote {
    product: string;
    partner?: string;
    methord?: string;
    price?: number | null;
    ExcelOffer?: any;
    payload?: any;
    term?: number;
    apr?: number;
    clonedQuoteDetails?:any;
    products?: selectedQuote[];
}

/** Detect new API shape: array of { FinancePartner, pricing: [...] } */
function isNewPricingFormat(raw: any): boolean {
    if (!Array.isArray(raw) || raw.length === 0) return false;
    const first = raw[0];
    return first && typeof first === 'object' && 'FinancePartner' in first && Array.isArray(first.pricing);
}

/** Matches System metrics / Utility card: monthly-derived or explicit annual, then Salesforce load. */
function resolvedUtilityAnnualKwh(
    base: Record<string, unknown>,
    sfAnnualUsage: number | null | undefined,
): number | null {
    return effectiveAnnualUsageKwhForPricing(base, sfAnnualUsage);
}

function positiveKwFromUnknown(v: unknown): number | null {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Live 3D / module-graph annual kWh (matches System metrics "Annual prod."). */
function viewerLiveAnnualKwh(viewerActual: number | undefined): number | null {
    if (viewerActual == null) return null;
    const n = Number(viewerActual);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n);
}

/** Live 3D system size kW DC (matches System metrics). */
function viewerLiveSystemKw(viewerTotal: number | undefined): number | null {
    if (viewerTotal == null) return null;
    const n = Number(viewerTotal);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

function isAiLayoutPricingDesign(design: SelectedDesignForPricing | null | undefined): boolean {
    return !!(design?.layoutKey && design.layoutKey !== 'products-solar');
}

/** Tree-cutting $ flow: metrics are passed explicitly; live 3D viewer is a different scenario. */
function isTreeCutLayoutPricingDesign(design: SelectedDesignForPricing | null | undefined): boolean {
    return typeof design?.layoutKey === 'string' && design.layoutKey.startsWith('tree-cut:');
}

/**
 * Inline Financing Options uses finalized design metrics (RetrieveSystemDesign).
 * Modal AI/tree $ uses explicit scenario selection via designForTrigger.
 * Modal products-solar $ also uses finalized snapshot (not live 3D preview).
 */
function preferFinalizedDesignMetrics(
    embeddedInPricingModal: boolean,
    design: SelectedDesignForPricing | null | undefined,
): boolean {
    if (isAiLayoutPricingDesign(design) || isTreeCutLayoutPricingDesign(design)) return false;
    if (!embeddedInPricingModal) return true;
    return !design || isFinalizedProductsSolarPricingDesign(design);
}

function resolveDesignForPricingPayload(
    embeddedInPricingModal: boolean,
    designForTrigger: SelectedDesignForPricing | null | undefined,
    selectedDesignForPricing: SelectedDesignForPricing | null | undefined,
): SelectedDesignForPricing | null {
    if (embeddedInPricingModal) {
        return designForTrigger ?? selectedDesignForPricing ?? null;
    }
    const sel = selectedDesignForPricing;
    if (
        sel &&
        !isAiLayoutPricingDesign(sel) &&
        !isTreeCutLayoutPricingDesign(sel) &&
        !isFinalizedProductsSolarPricingDesign(sel)
    ) {
        return sel;
    }
    return null;
}

/**
 * Products path: live viewer (finalized oneDRAW sim) first, then RetrieveSystemDesign, then selection / utility.
 * AI layout $: explicit selection first, then viewer, snapshot, utility.
 * Finalized inline grid: design snapshot first (matches System & Design), then viewer fallback.
 */
function resolvedSystemSizeKwForPayload(
    design: SelectedDesignForPricing | null | undefined,
    base: Record<string, unknown>,
    systemDesignSnapshot: Record<string, unknown> | null | undefined,
    viewerTotalSystemKw: number | undefined,
    preferDesignSnapshot = false,
): number {
    const fromSel = positiveKwFromUnknown(design?.systemSizeKw);
    const dd = systemDesignSnapshot ? parseDesignDataRecord(systemDesignSnapshot) : null;
    const fromSnap =
        systemDesignSnapshot != null ? systemSizeKwFromSnapshot(systemDesignSnapshot, dd) : null;
    const fromViewer = viewerLiveSystemKw(viewerTotalSystemKw);

    if (isTreeCutLayoutPricingDesign(design)) {
        if (fromSel != null) return fromSel;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromViewer != null) return fromViewer;
        return positiveKwFromUnknown(base.SystemSize) ?? 0;
    }

    if (isAiLayoutPricingDesign(design)) {
        if (fromSel != null) return fromSel;
        if (fromViewer != null) return fromViewer;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
    } else if (preferDesignSnapshot) {
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromViewer != null) return fromViewer;
        if (fromSel != null) return fromSel;
    } else {
        if (fromViewer != null) return fromViewer;
        if (fromSnap != null && fromSnap > 0) return fromSnap;
        if (fromSel != null) return fromSel;
    }
    return positiveKwFromUnknown(base.SystemSize) ?? 0;
}

/** Production (kWh/yr) from Design & Product Details or layout $ selection — preferred for pricing API over utility usage. */
function designAnnualProductionKwhFromSelection(
    design: SelectedDesignForPricing | null | undefined,
): number | null {
    if (!design) return null;
    const est = design.estimatedAnnualProductionKwh;
    if (est != null && Number.isFinite(est) && est > 0) return Math.round(est);
    const pw = design.productionW;
    if (pw != null && Number.isFinite(pw) && pw > 0) return Math.round(Number(pw));
    return null;
}

/**
 * Products: design snapshot first when preferDesignSnapshot (finalized inline grid), else live viewer first.
 * AI layout $: selection first, then viewer, snapshot, utility.
 */
function resolvedAnnualProductionKwhForPayload(
    design: SelectedDesignForPricing | null | undefined,
    base: Record<string, unknown>,
    sfAnnualUsage: number | null | undefined,
    systemDesignSnapshot: Record<string, unknown> | null | undefined,
    viewerActualAnnualKwh: number | undefined,
    preferDesignSnapshot = false,
    viewerModeCount?: number,
    siteViewerCalculationsPending?: boolean,
): number {
    if (
        preferDesignSnapshot &&
        !isAiLayoutPricingDesign(design) &&
        !isTreeCutLayoutPricingDesign(design) &&
        systemDesignSnapshot
    ) {
        const dd = parseDesignDataRecord(systemDesignSnapshot);
        const systemKw = systemSizeKwFromSnapshot(systemDesignSnapshot, dd);
        const multRaw = systemDesignSnapshot.Multiplier ?? systemDesignSnapshot.multiplier;
        const mult =
            multRaw != null && Number.isFinite(Number(multRaw)) && Number(multRaw) > 0
                ? Number(multRaw)
                : null;
        const fromDesign = resolveSolarAnnualProductionKwh({
            sd: systemDesignSnapshot,
            designData: dd,
            systemKw: systemKw ?? undefined,
            multiplier: mult,
            snapshotOnly: true,
        });
        if (fromDesign != null && fromDesign > 0) return fromDesign;
    }

    const fromSel = designAnnualProductionKwhFromSelection(design);
    const fromSnap = systemDesignSnapshot ? annualProductionKwhFromSnapshot(systemDesignSnapshot) : null;
    const fromViewer = viewerLiveAnnualKwh(viewerActualAnnualKwh);

    let k: number | null = null;
    if (isTreeCutLayoutPricingDesign(design)) {
        if (fromSel != null && fromSel > 0) k = fromSel;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromViewer != null) k = fromViewer;
    } else if (isAiLayoutPricingDesign(design)) {
        if (fromSel != null && fromSel > 0) k = fromSel;
        else if (fromViewer != null) k = fromViewer;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
    } else if (preferDesignSnapshot) {
        if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromViewer != null) k = fromViewer;
        else if (fromSel != null && fromSel > 0) k = fromSel;
    } else {
        if (fromViewer != null) k = fromViewer;
        else if (fromSnap != null && fromSnap > 0) k = fromSnap;
        else if (fromSel != null && fromSel > 0) k = fromSel;
    }
    if (k != null && k > 0) return k;
    const utilityK = resolvedUtilityAnnualKwh(base, sfAnnualUsage);
    if (utilityK != null) return utilityK;
    const est = design?.estimatedAnnualProductionKwh ?? base.AnnualUsage ?? 0;
    return Number(est) || 0;
}

const getOfferId = (offer: any) => {
    let offerId: string = ''
    if (Array.isArray(offer?.payload)) {
        offerId = offer?.payload[0]?.offerId ?? ''
    }
    return offerId
}

/** Parse new pricing API: use offer where Recommended === true per (partner, ProductType). */
function parseNewPricingResponse(apiData: any): { rows: FinancePartnerRow[]; raw: PricingPartnerBlock[] } {
    const raw = (apiData?.data ?? apiData) as PricingPartnerBlock[];
    const partnerMap: Record<string, FinancePartnerRow> = {};

    const initPrices = (): Record<string, PriceCell | null> => {
        const p: Record<string, PriceCell | null> = {};
        FINANCING_PRODUCTS.forEach((col) => { p[col] = null; });
        return p;
    };

    raw.forEach((block) => {
        const partner = normalizePartner(block.FinancePartner || '');
        if (!partnerMap[partner]) partnerMap[partner] = { partner, prices: initPrices() };

        (block.pricing || []).forEach((pricingItem) => {
            const productType = normalizeProductType(pricingItem.ProductType || '');
            if (!productType || !FINANCING_PRODUCTS.includes(productType)) return;

            const offers = pricingItem.Offer || [];
            const recommended = offers.find((o) => o.Recommended === true);
            if (!recommended) return;

            const partnerNorm = partner.toLowerCase();
            const isCash = partner === 'Cash' || (pricingItem.FinanceMethod || '').toUpperCase() === 'CASH';
            const monthly = isCash
                ? (typeof recommended.totalCost === 'number' ? recommended.totalCost : 0)
                : monthlyFromOffer(recommended, partnerNorm);
            if (recommended.__error) {
                return;
            }
            const { term, apr } = termAprFromOffer(recommended);
            const purchasePrice =
                partner === 'GoodLeap'
                    ? goodLeapPurchasePriceFromOffer(recommended, pricingItem.FinanceMethod || '')
                    : null;
            const offerId = getOfferId(recommended);
            partnerMap[partner].prices[productType] = {
                monthly: Number(monthly) || 0,
                apr,
                term,
                escalator: escalatorFromOffer(recommended),
                recommended: true,
                financeMethod: financeMethodToLabel(pricingItem.financeMethodName || ''),
                payload: recommended.payload,
                ExcelOffer: recommended?.ExcelOffer,
                ...(purchasePrice != null && purchasePrice > 0 ? { purchasePrice } : {}),
            };
        });
    });

    const rows = Object.values(partnerMap);
    applySingleRecommendedPerProductColumn(rows);
    return { rows, raw };
}
export interface FinancingGridCardProps {
    /** When true, the section is considered open (e.g. dashboard visible). */
    dashboardOpen?: boolean;
    /** When set (e.g. timestamp), re-run pricing load (e.g. $ / open grid). Uses cache when the payload key matches. */
    fetchTrigger?: number | null;
    /** When provided (e.g. from $ click), use this design for the payload so production/systemSize are correct. Overrides store. */
    designForTrigger?: SelectedDesignForPricing | null;
    /** Called when user clicks the refresh button to reload pricing. */
    priceQuote?: any;
    onRefreshRequested?: () => void;
    /** Rendered inside the pricing grid pop-out modal: no accordion, tighter chrome, summary + actions on one row when space allows. */
    embeddedInPricingModal?: boolean;
}


export const FinancingGridCard: React.FC<FinancingGridCardProps> = ({
    dashboardOpen = true,
    fetchTrigger = null,
    designForTrigger = null,
    priceQuote = null,
    onRefreshRequested,
    embeddedInPricingModal = false,
}) => {
    const {
        financingGrid,
        setFinancingGrid,
        selectedFinanceCells,
        toggleFinanceCell,
        addFinanceCell,
        setPricingSelectionLoading,
        setQuotesRefreshTrigger,
        setCommerceHubProductsOpen,
        setCommerceHubQuotesOpen,
    } = useOneButtonStore();
    const networkRefreshTrigger = useOneButtonStore((s) => s.pricingNetworkRefreshTrigger);
    const [sectionOpen, setSectionOpen] = useSunnyFocusedSectionOpen('financing', {
        enabled: !embeddedInPricingModal,
        fallbackOpen: true,
    });
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [financingView, setFinancingView] = useState<'combined' | 'grid'>('combined');
    const [viewAllOpen, setViewAllOpen] = useState(false);
    const [allPricingRaw, setAllPricingRaw] = useState<PricingPartnerBlock[] | null>(null);
    const [confirmQuoteCellKey, setConfirmQuoteCellKey] = useState<selectedQuote | null>(null);
    const [quoteForm, setQuoteForm] = useState<QuoteFormState | null>(null);
    const [solarQuoteForm, setSolarQuoteForm] = useState<SolarQuoteFormState | null>(null);
    const theme = useOneButtonStore((s) => s.theme);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const utilityCompanyName = useOneButtonStore((s) => s.utilityCompanyName);
    const selectedDesignForPricing = useOneButtonStore((s) => s.selectedDesignForPricing);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const SFannualUsage = useViewerStore((s) => s.SFannualUsage);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const totalSystemSize = useViewerStore((s) => s.totalSystemSize);
    const viewerModeCount = useViewerStore((s) => s.modeCount);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const layoutMetricsByKey = useViewerStore((s: any) => s.layoutMetricsByKey);
    const liveMultiplier = useViewerStore((s) => s.multiplier);
    const liveModuleMultiplierAverage = useViewerStore((s) => s.moduleMultiplierAverage);
    const numericOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const pricingCacheRef = useRef<Record<string, any>>({});
    const pricingInFlightRef = useRef<Record<string, Promise<unknown>>>({});
    const lastUserFetchTriggerRef = useRef<number | null>(null);
    const lastConsumedUtilityCodeRef = useRef<string | null>(null);
    const siteIdRef = useRef<string>('');
    const lastConsumedNetworkRefreshRef = useRef<number | null>(null);

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const generateQuoteAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Generate_Quote_Access');
    const isGenerateQuoteDisabled = generateQuoteAccess !== 'Edit' && generateQuoteAccess !== 'View';
    /** Generate quote from grid cells is only for inline Financing Options — not the pricing modal opened via layout $ */
    const isGenerateQuoteFromGridDisabled = isGenerateQuoteDisabled || embeddedInPricingModal;

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 3 });
    const notifClass = `obd-notification obd-theme-${theme}`;

    const currentSiteId = useMemo(() => {
        const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        return (
            qs?.get('sfid') ||
            (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ||
            'unknown-site'
        );
    }, []);

    const showTreeCutCostColumn = useMemo(() => {
        // Only show TREE column when pricing was opened from a tree-cutting scenario ($ click).
        return embeddedInPricingModal && isTreeCutLayoutPricingDesign(designForTrigger);
    }, [embeddedInPricingModal, designForTrigger]);

    const designForPricingPayload = useMemo(
        () => resolveDesignForPricingPayload(embeddedInPricingModal, designForTrigger, selectedDesignForPricing),
        [embeddedInPricingModal, designForTrigger, selectedDesignForPricing],
    );

    /** Inline Financing Options for the finalized design — not modal AI/tree scenario context. */
    const isFinalizedInlineFinancing = !embeddedInPricingModal && !designForPricingPayload;

    const useFinalizedDesignMetrics = preferFinalizedDesignMetrics(
        embeddedInPricingModal,
        designForPricingPayload,
    );

    /** Finalized grid: only refetch when RetrieveSystemDesign metrics change — not live 3D preview. */
    const finalizedDesignMetricsKey = useMemo(() => {
        if (!useFinalizedDesignMetrics || !systemDesign) return '';
        const snap = systemDesign as Record<string, unknown>;
        const dd = parseDesignDataRecord(snap);
        const kw = systemSizeKwFromSnapshot(snap, dd);
        const prod = annualProductionKwhFromSnapshot(snap);
        const mult = snap.Multiplier ?? snap.multiplier;
        return `${kw ?? ''}|${prod ?? ''}|${mult ?? ''}`;
    }, [useFinalizedDesignMetrics, systemDesign]);

    const pricingMetricsDep = useMemo(() => {
        if (useFinalizedDesignMetrics) return `finalized:${finalizedDesignMetricsKey}`;
        return `live:${totalSystemSize ?? ''}|${actualWattage ?? ''}`;
    }, [useFinalizedDesignMetrics, finalizedDesignMetricsKey, totalSystemSize, actualWattage]);

    const treeCutCostForGrid = useMemo((): number | null => {
        if (!showTreeCutCostColumn) return null;
        const d = designForPricingPayload;
        const raw = (d as any)?.TreeCutCost ?? (d as any)?.treeCutCost ?? (d as any)?.cutCost ?? null;
        const n = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
        return n != null && n >= 0 ? n : null;
    }, [showTreeCutCostColumn, designForPricingPayload]);

    useEffect(() => {
        if (siteIdRef.current && siteIdRef.current !== currentSiteId) {
            pricingCacheRef.current = {};
        }
        siteIdRef.current = currentSiteId;
    }, [currentSiteId]);

    useEffect(() => {
        const userTriggeredFetch = fetchTrigger != null && fetchTrigger > 0;
        const shouldFetch = dashboardOpen || userTriggeredFetch;
        if (!shouldFetch) return;
        const refreshBypassNetwork =
            networkRefreshTrigger != null &&
            networkRefreshTrigger > 0 &&
            networkRefreshTrigger !== lastConsumedNetworkRefreshRef.current;
        const base = (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>;
        const currentUtilityCode = resolveUtilityCompanyCode(base, utilityCompanyName);
        const utilityInputsChanged =
            lastConsumedUtilityCodeRef.current != null &&
            lastConsumedUtilityCodeRef.current !== currentUtilityCode;
        /* $ / modal: one request per fetchTrigger bump (design may update in the prior render). */
        if (!dashboardOpen && userTriggeredFetch && !refreshBypassNetwork && !utilityInputsChanged) {
            if (lastUserFetchTriggerRef.current === fetchTrigger) return;
            lastUserFetchTriggerRef.current = fetchTrigger;
        }
        /* When user clicks $ we must fetch even if solarUtilityData isn't loaded yet; use selectedDesignForPricing. */
        if (!solarUtilityData && !userTriggeredFetch) return;
        setLoadError(null);
        setLoading(true);
        setPricingSelectionLoading(true);
        const designToUse = designForPricingPayload;
        const preferDesignSnapshot = useFinalizedDesignMetrics;
        const viewerKwForPayload = preferDesignSnapshot ? undefined : totalSystemSize;
        const viewerProdForPayload = preferDesignSnapshot ? undefined : actualWattage;
        const sdSnap = systemDesign ? (systemDesign as Record<string, unknown>) : null;
        const systemSizeNum = resolvedSystemSizeKwForPayload(
            designToUse,
            base,
            sdSnap,
            viewerKwForPayload,
            preferDesignSnapshot,
        );
        const estimatedAnnualProductionValue = resolvedAnnualProductionKwhForPayload(
            designToUse,
            base,
            SFannualUsage,
            sdSnap,
            viewerProdForPayload,
            preferDesignSnapshot,
            viewerModeCount,
            siteViewerCalculationsPending,
        );
        const estProdNum = Number(estimatedAnnualProductionValue);
        const pricingProducts = activeOpportunityProductTypeNames(opportunityDetails);
        const requiresSolarMetrics = pricingPayloadRequiresSolarMetrics({ products: pricingProducts });
        if (requiresSolarMetrics) {
            if (!Number.isFinite(estProdNum) || estProdNum <= 0) {
                const msg = 'Estimated annual production must be greater than 0 to price this design.';
                setLoadError(msg);
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
            if (!Number.isFinite(systemSizeNum) || systemSizeNum <= 0) {
                const msg = 'System size must be greater than 0 to price this design.';
                setLoadError(msg);
                setLoading(false);
                setPricingSelectionLoading(false);
                return;
            }
        }
        const roofSquareFromDesign = systemDesign ? getRoofSquaresFromSystemDesign(systemDesign) : null;
        const roofSquare = priceQuoteOverrides?.roofSquareOverride ?? roofSquareFromDesign ?? 0;
        const lowSlope = priceQuoteOverrides?.lowSlopeOverride ?? 0;
        const normalSlope = priceQuoteOverrides?.normalSlopeOverride ?? 0;
        const roofPitch = normalSlope > 0 && (lowSlope == null || lowSlope === 0)
            ? 'Normal Slope'
            : (lowSlope > 0 ? 'Low Slope' : 'Normal Slope');
        const lowRoof = Math.round(Number(lowSlope) || 0);
        const standardRoof = Math.round(Number(normalSlope) || 0);
        const batteryCountRaw = (systemDesign as any)?.BatteryInformation?.Quantity;
        const batteryCount =
            batteryCountRaw != null && Number.isFinite(Number(batteryCountRaw))
                ? Math.max(0, Math.round(Number(batteryCountRaw)))
                : 0;
        const treeCutCostForPayloadRaw =
            isTreeCutLayoutPricingDesign(designToUse) || (embeddedInPricingModal && isTreeCutLayoutPricingDesign(designForTrigger ?? null))
                ? (designToUse as any)?.TreeCutCost ?? null : null;
        const treeCutCostForPayload =
            treeCutCostForPayloadRaw != null && Number.isFinite(Number(treeCutCostForPayloadRaw))
                ? Number(treeCutCostForPayloadRaw)
                : null;
        const pricingPayload = {
            state: String(base.StateId ?? ''),
            systemSize: systemSizeNum,
            estimatedAnnualProduction: Math.round(estProdNum),
            utilityCompanyCode: currentUtilityCode,
            batteryCount,
            roofPitch,
            lowRoof,
            standardRoof,
            products: pricingProducts,
            ...(treeCutCostForPayload != null ? { TreeCutCost: treeCutCostForPayload } : {}),
        };

        const pricingCacheKey = `${currentSiteId}::${JSON.stringify(pricingPayload)}`;
        const cacheStorageKey = `obd-pricing-cache:${pricingCacheKey}`;
        const usePricingCache = !refreshBypassNetwork && !utilityInputsChanged;
        lastConsumedUtilityCodeRef.current = currentUtilityCode;

        const applyPricingBody = (body: any) => {
            const { rows, raw: blocksRaw } = parseNewPricingResponse(body);
            const blocks =
                treeCutCostForPayload != null
                    ? (blocksRaw || []).map((b: any) => ({
                          ...b,
                          pricing: (b?.pricing || []).map((p: any) => ({
                              ...p,
                              Offer: (p?.Offer || []).map((o: any) => ({
                                  ...o,
                                  TreeCutCost: o?.TreeCutCost ?? treeCutCostForPayload,
                              })),
                          })),
                      }))
                    : blocksRaw;
            setFinancingGrid(rows);
            setAllPricingRaw(blocks);
        };

        const inMemoryCached = usePricingCache ? pricingCacheRef.current[pricingCacheKey] : undefined;
        if (inMemoryCached) {
            applyPricingBody(inMemoryCached);
            setLoading(false);
            setPricingSelectionLoading(false);
            return;
        }

        if (usePricingCache && typeof window !== 'undefined') {
            try {
                const serialized = sessionStorage.getItem(cacheStorageKey);
                if (serialized) {
                    const cachedBody = JSON.parse(serialized);
                    pricingCacheRef.current[pricingCacheKey] = cachedBody;
                    applyPricingBody(cachedBody);
                    setLoading(false);
                    setPricingSelectionLoading(false);
                    return;
                }
            } catch {
                // Ignore cache parse/storage errors and fallback to network.
            }
        }

        if (refreshBypassNetwork) {
            lastConsumedNetworkRefreshRef.current = networkRefreshTrigger;
        }

        const inflight = pricingInFlightRef.current[pricingCacheKey];
        if (inflight) {
            void inflight.then((body) => {
                if (body) applyPricingBody(body);
                setLoading(false);
                setPricingSelectionLoading(false);
            });
            return;
        }

        const pricingRequest = quoteServices
            .getPricing(pricingPayload)
            .then((response) => {
                const body = response?.data;
                pricingCacheRef.current[pricingCacheKey] = body;
                if (typeof window !== 'undefined') {
                    try {
                        sessionStorage.setItem(cacheStorageKey, JSON.stringify(body));
                    } catch {
                        // Ignore quota/storage issues.
                    }
                }
                applyPricingBody(body);
                return body;
            })
            .catch((err) => {
                setFinancingGrid([]);
                setAllPricingRaw(null);
                const message = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to load pricing';
                setLoadError(String(message));
                throw err;
            })
            .finally(() => {
                delete pricingInFlightRef.current[pricingCacheKey];
                setLoading(false);
                setPricingSelectionLoading(false);
            });

        pricingInFlightRef.current[pricingCacheKey] = pricingRequest;
    }, [
        dashboardOpen,
        fetchTrigger,
        networkRefreshTrigger,
        embeddedInPricingModal,
        designForPricingPayload,
        solarUtilityData,
        utilityCompanyName,
        systemDesign,
        setPricingSelectionLoading,
        SFannualUsage,
        pricingMetricsDep,
        viewerModeCount,
        siteViewerCalculationsPending,
        isFinalizedInlineFinancing,
        useFinalizedDesignMetrics,
        currentSiteId,
        numericOpportunityId,
        opportunityDetails,
    ]);

    /** Always show all partners and product columns; merge API rows with default partner list, fill missing with null → N/A */
    const displayGridRows = useMemo((): FinancePartnerRow[] => {
        const initPrices = (): Record<string, PriceCell | null> => {
            const p: Record<string, PriceCell | null> = {};
            FINANCING_PRODUCTS.forEach((col) => { p[col] = null; });
            return p;
        };
        const defaultRows = DEFAULT_FINANCE_PARTNERS.map((partner) => {
            const fromApi = financingGrid.find((r) => r.partner === partner);
            return fromApi ?? { partner, prices: initPrices() };
        });
        const apiOnlyPartners = financingGrid.filter((r) => !DEFAULT_FINANCE_PARTNERS.includes(r.partner));
        return [...defaultRows, ...apiOnlyPartners];
    }, [financingGrid]);

    const combinedFinancingSummary = useMemo(
        () => buildRecommendedCombinedFinancing(displayGridRows),
        [displayGridRows],
    );

    const combinedMonthlyDisplay =
        combinedFinancingSummary.totalMonthly > 0
            ? `$${combinedFinancingSummary.totalMonthly.toFixed(2)}/mo`
            : loading
              ? '…'
              : '—';

    /** Flat rows for "All pricing options" table: partner, product, method, monthly, loan, term, APR, escalator, status */
    const viewAllRows = useMemo((): PricingViewAllRow[] => {
        if (!Array.isArray(allPricingRaw) || allPricingRaw.length === 0) return [];
        const designForHeader = designForPricingPayload;
        const treeDetailsText =
            isTreeCutLayoutPricingDesign(designForHeader) &&
            Array.isArray((designForHeader as any)?.treeDetails) &&
            (designForHeader as any).treeDetails.length > 0
                ? (designForHeader as any).treeDetails
                      .map((t: any) => {
                          const id = String(t?.treeId || '').trim();
                          const dbh = String(t?.dbh || '').trim();
                          const h = String(t?.height || '').trim();
                          const stump = String(t?.stump || '').trim();
                          const bits = [
                              id ? `#${id}` : '',
                              dbh ? `DBH ${dbh}` : '',
                              h ? `H ${h}` : '',
                              stump ? `Stump ${stump}` : '',
                          ].filter(Boolean);
                          return bits.join(' · ');
                      })
                      .filter(Boolean)
                      .join(' | ')
                : undefined;
        const rows: PricingViewAllRow[] = [];
        allPricingRaw.forEach((block) => {
            const partner = normalizePartner(block.FinancePartner || '');
            const partnerNorm = (block.FinancePartner || '').toLowerCase();
            (block.pricing || []).forEach((pricingItem) => {
                const product = normalizeProductType(pricingItem.ProductType || '') ?? pricingItem.ProductType ?? 'Solar';
                const financeMethod = financeMethodToLabel(pricingItem.FinanceMethod || '');
                const offers = pricingItem.Offer || [];
                offers.forEach((offer) => {
                    const err = offer?.__error;
                    const friendlyError = err
                        ? (err.includes('Missing required fields') || err.includes('required')
                            ? 'Pricing unavailable'
                            : 'Pricing unavailable')
                        : null;
                    const recommended = offer.Recommended === true && !err;
                    const monthly = err ? null : monthlyFromOffer(offer, partnerNorm);
                    const loanAmount = loanAmountFromOffer(offer);
                    const { term, apr } = termAprFromOffer(offer);
                    const escalatorPct = escalatorFromOffer(offer);
                    const treeCutCost = err ? null : treeCutCostFromOffer(offer);
                    const minimumRate = offer?.ExcelOffer?.startingRate ?? null;
                    const rateCap = offer?.ExcelOffer?.rateCap ?? null;
                    rows.push({
                        partner,
                        product,
                        financeMethod,
                        treeDetailsText,
                        treeCutCost,
                        monthly: monthly != null && monthly > 0 ? monthly : null,
                        loanAmount,
                        term: term > 0 ? term : null,
                        apr: apr > 0 ? apr : null,
                        escalatorPct,
                        minimumRate,
                        rateCap,
                        recommended,
                        error: friendlyError,
                    });
                });
            });
        });
        applySingleRecommendedViewAllRows(rows);
        return rows;
    }, [allPricingRaw, designForPricingPayload]);

    const cardMod = embeddedInPricingModal ? ' obd-card--financing--pricing-modal' : '';

    if (loading) {
        return (
            <div className={`obd-card obd-card--financing${cardMod}`}>
                {!embeddedInPricingModal && (
                    <span className="obd-card__label obd-card__label--section-icon">
                        <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                            <CurrencyCircleDollar size={16} weight="duotone" />
                        </span>
                        Financing Options for the Finalized Design
                    </span>
                )}
                <div className="obd-financing__loading">Loading pricing data…</div>
            </div>
        );
    }

    const base = (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>;
    const designForHeader = designForPricingPayload;
    const sdHeader = systemDesign ? (systemDesign as Record<string, unknown>) : null;
    const selectedDesignKw = useFinalizedDesignMetrics ? undefined : totalSystemSize;
    const selectedDesignProd = useFinalizedDesignMetrics ? undefined : actualWattage;
    const headerSystemKwNum = resolvedSystemSizeKwForPayload( designForHeader, base, sdHeader, selectedDesignKw, 
        useFinalizedDesignMetrics
    );
    const estimatedKwh = designForHeader?.estimatedAnnualProductionKwh ?? base.AnnualUsage ?? 0;
    const headerUtilityAnnual = resolvedUtilityAnnualKwh(base, SFannualUsage);
    const fromSelH = designAnnualProductionKwhFromSelection(designForHeader);
    const fromSnapH = sdHeader ? annualProductionKwhFromSnapshot(sdHeader) : null;
    const fromViewerH = viewerLiveAnnualKwh(selectedDesignProd);
    let layoutAnnualKwh: number | null = null;
    let headerProductionIsUtility = false;
    let headerProductionFromLiveViewer = false;
    let headerProductionFromTreeScenario = false;
    if (isTreeCutLayoutPricingDesign(designForHeader)) {
        if (fromSelH != null && fromSelH > 0) {
            layoutAnnualKwh = fromSelH;
            headerProductionFromTreeScenario = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        }
    } else if (isAiLayoutPricingDesign(designForHeader)) {
        if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
    } else if (useFinalizedDesignMetrics && sdHeader) {
        const ddHeader = parseDesignDataRecord(sdHeader);
        const snapKwHeaderResolved = systemSizeKwFromSnapshot(sdHeader, ddHeader);
        const multRawHeader = sdHeader.Multiplier ?? sdHeader.multiplier;
        const multHeader =
            multRawHeader != null && Number.isFinite(Number(multRawHeader)) && Number(multRawHeader) > 0
                ? Number(multRawHeader)
                : null;
        const fromDesignHeader = resolveSolarAnnualProductionKwh({
            sd: sdHeader,
            designData: ddHeader,
            systemKw: snapKwHeaderResolved ?? undefined,
            multiplier: multHeader,
            snapshotOnly: true,
        });
        if (fromDesignHeader != null && fromDesignHeader > 0) {
            layoutAnnualKwh = fromDesignHeader;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
    } else {
        if (fromViewerH != null) {
            layoutAnnualKwh = fromViewerH;
            headerProductionFromLiveViewer = true;
        } else if (fromSnapH != null && fromSnapH > 0) layoutAnnualKwh = fromSnapH;
        else if (fromSelH != null && fromSelH > 0) layoutAnnualKwh = fromSelH;
    }
    if (layoutAnnualKwh == null || layoutAnnualKwh <= 0) {
        if (headerUtilityAnnual != null) {
            layoutAnnualKwh = headerUtilityAnnual;
            headerProductionIsUtility = true;
        }
    }
    const ddHeader = sdHeader ? parseDesignDataRecord(sdHeader) : null;
    const snapKwHeader = sdHeader ? systemSizeKwFromSnapshot(sdHeader, ddHeader) : null;
    const viewerKwHeader = viewerLiveSystemKw(selectedDesignKw);
    const designKwPositive =
        positiveKwFromUnknown(designForHeader?.systemSizeKw) != null ||
        (snapKwHeader != null && snapKwHeader > 0);
    const utilityOnlySize =
        headerSystemKwNum > 0 &&
        positiveKwFromUnknown(designForHeader?.systemSizeKw) == null &&
        (snapKwHeader == null || snapKwHeader <= 0) &&
        (viewerKwHeader == null || viewerKwHeader <= 0) &&
        positiveKwFromUnknown(base.SystemSize) != null;
    const systemSizeSource = isTreeCutLayoutPricingDesign(designForHeader)
        ? 'Tree-cutting scenario: system size from the scenario row (DC kW) passed when you clicked $.'
        : utilityOnlySize
            ? 'Opportunity utility system size (SolarUtility.SystemSize).'
            : designForHeader?.layoutKey === 'products-solar' || !isAiLayoutPricingDesign(designForHeader)
                ? useFinalizedDesignMetrics
                    ? 'Design & Product Details: system size from RetrieveSystemDesign snapshot (matches System & Design).'
                    : 'Design & Product Details: system size from design API / arrays, live 3D design, or utility when needed.'
                : 'Selected AI layout: module count × module power ÷ 1000.';
    const systemHeader = {
        state: String(base.StateId ?? '—'),
        systemSize: headerSystemKwNum > 0 ? headerSystemKwNum.toFixed(2) : '—',
        systemSizeTitle: systemSizeSource,
        estimatedAnnualProduction:
            layoutAnnualKwh != null
                ? `${layoutAnnualKwh.toLocaleString()} kWh ${headerProductionIsUtility
                    ? '(utility annual)'
                    : headerProductionFromTreeScenario
                        ? '(tree-cutting scenario)'
                        : headerProductionFromLiveViewer
                            ? '(finalized design)'
                            : '(design production)'
                }`
                : headerUtilityAnnual != null
                    ? `${headerUtilityAnnual.toLocaleString()} kWh (utility annual)`
                    : estimatedKwh != null && Number.isFinite(Number(estimatedKwh)) && Number(estimatedKwh) > 0
                        ? `${Number(estimatedKwh).toLocaleString()} kWh`
                        : '—',
        estimatedProductionTitle:
            layoutAnnualKwh != null
                ? headerProductionIsUtility
                    ? 'Utility annual usage (fallback when design production is not on file). Sent to the pricing API.'
                    : headerProductionFromTreeScenario
                        ? 'Annual kWh estimated for this tree-cutting scenario (scaled from site usage or layout production vs baseline DC watts). Sent to the pricing API.'
                        : headerProductionFromLiveViewer
                            ? 'Annual production from the live oneDRAW 3D design (same as System metrics). Sent to the pricing API.'
                            : 'Estimated annual production (kWh) from solar design data or the layout you priced with $. Sent to the pricing API.'
                : headerUtilityAnnual != null
                    ? 'Utility annual usage (used when no design production is available). Sent to the pricing API.'
                    : 'No design production or utility annual usage on file.',
        utilityCompanyCode: resolveUtilityCompanyCode(base, utilityCompanyName) || '—',
    };

    const contentOpen = embeddedInPricingModal || sectionOpen;
    const wrapMod = embeddedInPricingModal ? ' obd-financing__wrap--pricing-modal' : '';
    const productionDisplay = splitFinancingProductionLabel(String(systemHeader.estimatedAnnualProduction || '—'));
    const productionNoteLabel = productionDisplay.note ? formatFinancingProductionNote(productionDisplay.note) : null;
    const systemSizeDisplay =
        systemHeader.systemSize !== '—' ? `${systemHeader.systemSize} kW` : '—';
    const isLiveDesign = !designForHeader || designForHeader.layoutKey === 'products-solar';
    const aiMultiplier = designForHeader?.layoutKey ? layoutMetricsByKey?.[designForHeader.layoutKey]?.multiplier : null;
    let multiplierDisplay = '—';
    if (isLiveDesign) {
        const liveVal = Number(sdHeader?.Multiplier) || liveModuleMultiplierAverage || liveMultiplier;
        if (liveVal && liveVal > 0) multiplierDisplay = liveVal.toFixed(2);
    } else {
        const aiVal = aiMultiplier ?? designForHeader?.multiplier;
        if (aiVal && aiVal > 0) multiplierDisplay = aiVal.toFixed(4);
    }

    const systemHeaderBlock = (
        <div
            className="obd-financing__system-header obd-financing__system-header--tiles"
            aria-label="Pricing parameters"
        >
            <div className="obd-financing__system-tiles" role="list">
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--state"
                    role="listitem"
                    title={`Project state: ${systemHeader.state}`}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--state" aria-hidden>
                        <MapPin size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">State</span>
                        <span className="obd-financing__system-tile-value">{systemHeader.state}</span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--system"
                    role="listitem"
                    title={systemHeader.systemSizeTitle}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--system" aria-hidden>
                        <Lightning size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">System</span>
                        <span className="obd-financing__system-tile-value">{systemSizeDisplay}</span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--utility"
                    role="listitem"
                    title={`Utility: ${systemHeader.utilityCompanyCode}`}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--utility" aria-hidden>
                        <Plug size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">Utility</span>
                        <span className="obd-financing__system-tile-value obd-financing__system-tile-value--clamp">
                            {systemHeader.utilityCompanyCode}
                        </span>
                    </div>
                </div>
                <div
                    className="obd-financing__system-tile obd-financing__system-tile--production"
                    role="listitem"
                    title={systemHeader.estimatedProductionTitle}
                >
                    <span className="obd-financing__system-tile-icon obd-financing__system-tile-icon--production" aria-hidden>
                        <Sun size={16} weight="duotone" />
                    </span>
                    <div className="obd-financing__system-tile-body">
                        <span className="obd-financing__system-tile-label">Est. production</span>
                        <span className="obd-financing__system-tile-value-row">
                            <span className="obd-financing__system-tile-value">{productionDisplay.value}</span>
                            {productionNoteLabel ? (
                                <span className="obd-financing__system-tile-badge">{productionNoteLabel}</span>
                            ) : null}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const toolbarBlock =
        (allPricingRaw && allPricingRaw.length > 0) || onRefreshRequested ? (
            <div
                className="obd-financing__toolbar obd-metrics__action-group"
                role="group"
                aria-label="Financing actions"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="obd-merged-system__chip-btn obd-financing__toolbar-products-btn"
                    data-obd-guide="commerce-products"
                    onClick={() => setCommerceHubProductsOpen(true)}
                >
                    <Cube size={16} weight="bold" aria-hidden />
                    <span className="obd-financing__products-label">Products</span>
                </button>
                {allPricingRaw && allPricingRaw.length > 0 && (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn obd-financing__view-all-btn"
                        onClick={() => setViewAllOpen(true)}
                    >
                        <ListBullets size={16} weight="bold" aria-hidden />
                        <span className="obd-financing__view-all-label">View all pricing options</span>
                    </button>
                )}
                {onRefreshRequested && (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn obd-financing__refresh-btn obd-financing__refresh-btn--toolbar"
                        title="Refresh pricing"
                        aria-label="Refresh pricing"
                        onClick={() => onRefreshRequested()}
                    >
                        <ArrowsClockwise size={16} weight="bold" aria-hidden />
                        <span className="obd-financing__refresh-label">Refresh</span>
                    </button>
                )}
            </div>
        ) : null;

    const openGenerateQuoteModel = async (row: FinancePartnerRow, col: FinancingProduct) => {
        if (isGenerateQuoteFromGridDisabled) return;
        if (col == "Solar" || col == "Roof" || col == "Battery") {

            const product = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === col));
            const activeProducts = (opportunityDetails.OpportunityProduct.filter((ele: any) => ele.ActiveFlag));
            if (product) {
                if(activeProducts.length > 1){
                    const solarLRProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Solar" && ele.Finance_Partner_Display_Name == 'Lightreach' && ele.ActiveFlag ));
                    const BatteryLRProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Battery" && ele.Finance_Partner_Display_Name == 'Lightreach' && ele.ActiveFlag ));
                    if(col !=='Roof' && solarLRProduct && BatteryLRProduct){
                        noticeApi.warning({
                            message: 'Invalid Quote Combination',
                            description: 'If the finance partner of Solar and Battery are Lightreach then '+ col +' single quote can\'t create',
                            className: notifClass,
                        });
                        return;
                    }

                    const solarGLProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Solar" && ele.Finance_Partner_Display_Name == 'Lightreach' && ele.ActiveFlag ));
                    const BatteryGLProduct = (opportunityDetails.OpportunityProduct.find((ele: any) => ele.Product_Type_Name === "Battery" && ele.Finance_Partner_Display_Name == 'Lightreach' && ele.ActiveFlag ));
                    
                    if(solarGLProduct && BatteryGLProduct){
                        if(solarGLProduct.Finance_Method_Name === BatteryGLProduct.Finance_Method_Name && (solarGLProduct.Finance_Method_Name == 'Lease Storage' || solarGLProduct.Finance_Method_Name == 'Loan Storage'))
                            noticeApi.warning({
                                message: 'Invalid Quote Combination',
                                description: 'If the finance partner of Solar and Battery are Goodleap with '+ solarGLProduct.Finance_Method_Name +' then '+ col +' single quote can\'t create',
                                className: notifClass,
                            });
                        
                    } 

                }
                if (product.Finance_Partner_Display_Name === 'Good Leap') product.Finance_Partner_Display_Name = 'GoodLeap';
                if (normalizePartner(product.Finance_Partner_Display_Name) == row.partner) {
                    const priceCell = row.prices[col];
                    const isCash = row.partner === 'Cash';

                    const currentOppId = useOneButtonStore.getState().opportunityID;
                    const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
                    const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);

                    if(!isCash && !!product.Finance_Method_Name 
                        && (row.prices[product.Product_Type_Name] as any).financeMethod !== product.Finance_Method_Name){
                        noticeApi.warning({
                            message: 'Finance Method Missmatch',
                            description: 'Selected fiance method does not match with sugested finance method. Please update the finance method accordingly.',
                            className: notifClass,
                        });
                        return;
                    }                    
                    if (Array.isArray(syncList) && !isCash) {
                        let haveAnyIGSLightreach = false;
                        opportunityDetails.OpportunityProduct.forEach((opportunityProduct: any) => {
                            if ((opportunityProduct.Finance_Partner_Display_Name == 'IGS' || opportunityProduct.Finance_Partner_Display_Name.toLowerCase() == 'lightreach') && opportunityProduct.ActiveFlag) {
                                haveAnyIGSLightreach = true;
                            }
                        });
                        if ((haveAnyIGSLightreach &&  syncList.length > 0) ) {
                            const statusObj = syncList[0];
                            let solarPartnerName = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag)?.Finance_Partner_Display_Name ?? '';
                            const newStatus: Partial<PartnerSyncChecklist> = {
                                lead: statusObj.IsLeadCreate === true,
                                contact: statusObj.IsContactSync === true,
                                titleCheck: (statusObj.IsTilte === true || statusObj.IsTitle === true),
                                utility: statusObj.IsUtility === true,
                                leadSystem: statusObj.IsLeadSystem === true,
                                creditCheck: statusObj.IsCreditPass === true,
                            };

                            if(newStatus.lead === false || newStatus.contact === false  || newStatus.utility === false || newStatus.leadSystem === false){
                                noticeApi.warning({
                                    message: 'Finance Partner Sync required',
                                    description: 'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                                    className: notifClass,
                                });
                                return;
                            }
                        }
                        
                    } else if(!isCash) {
                        noticeApi.warning({
                            message: 'Finance Partner Sync required',
                            description: 'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                            className: notifClass,
                        });
                        return;
                    }

                    const data: selectedQuote = {
                        product: col,
                        partner: row.partner,
                        methord: priceCell?.financeMethod || '',
                        price: priceCell?.purchasePrice || null,
                        ExcelOffer: priceCell?.ExcelOffer || null,
                        payload: priceCell?.payload || null,
                        term: priceCell?.term,
                        apr: priceCell?.apr,

                    };
                    setConfirmQuoteCellKey(data);


                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Mismatched',
                        description: 'Finance partner does not match with selected product',
                        className: notifClass,
                    });
                }
            } else {
                noticeApi.warning({
                    message: 'Product not selected',
                    description: 'Product not find in the selected products',
                    className: notifClass,
                });
            }
        } else if (col == "Solar + Battery") {
            debugger;
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Solar" || ele.Product_Type_Name ===  "Battery") && ele.ActiveFlag)));
            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                   
                    const solarProduct = product.find((ele: any) => ele.Product_Type_Name === "Solar");
                    let solarFinance_Method_Name= solarProduct?.Finance_Method_Name;

                    const batteryProduct = product.find((ele: any) => ele.Product_Type_Name === "Battery");
                    let batteryFinance_Method_Name = batteryProduct?.Finance_Method_Name;
                    const isCash = row.partner === 'Cash';
                    if(!isCash && (solarFinance_Method_Name !== batteryFinance_Method_Name || row?.prices[col]?.financeMethod !== batteryFinance_Method_Name || row?.prices[col]?.financeMethod !== solarFinance_Method_Name)){
                        noticeApi.warning({
                            message: 'Finance Methord Mismatched',
                            description: 'Finance methord does not match with selected product',
                            className: notifClass,
                        });
                        return;
                    }
                   
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[col];
                        let financeMethod = priceCell?.financeMethod;
                        let ExcelOffer = priceCell?.ExcelOffer;
                        if (ele.Product_Type_Name === "Battery" && priceCell?.financeMethod === "Loan") {
                            financeMethod = "Battery Loan"
                        }
                        if(ele.Product_Type_Name === "Battery"){
                            ExcelOffer = row.prices.Solar?.ExcelOffer;
                        }
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Mismatched',
                        description: 'Finance partner does not match with selected product',
                        className: notifClass,
                    });
                }

            } else {
                noticeApi.warning({
                    message: 'Product not selected',
                    description: 'Product not find in the selected products',
                    className: notifClass,
                });
            }
        } else if(col == "Solar + Roof"){
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Solar" || ele.Product_Type_Name ===  "Roof")) && ele.ActiveFlag));
            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                    
                    const solarProduct = product.find((ele: any) => ele.Product_Type_Name === "Solar");
                    let solarFinance_Method_Name= solarProduct?.Finance_Method_Name;

                    const roofProduct = product.find((ele: any) => ele.Product_Type_Name === "Roof");
                    let roofFinance_Method_Name = roofProduct?.Finance_Method_Name;
                    const isCash = row.partner === 'Cash';
                    if(!isCash && (solarFinance_Method_Name !== roofFinance_Method_Name || row?.prices[col]?.financeMethod !== roofFinance_Method_Name || row?.prices[col]?.financeMethod !== solarFinance_Method_Name)){
                        noticeApi.warning({
                            message: 'Finance Methord Mismatched',
                            description: 'Finance methord does not match with selected product',
                            className: notifClass,
                        });
                        return;
                    }
                    
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[col];
                        let financeMethod = priceCell?.financeMethod;
                        let ExcelOffer = priceCell?.ExcelOffer;
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Mismatched',
                        description: 'Finance partner does not match with selected product',
                        className: notifClass,
                    });
                }

            } else {
                noticeApi.warning({
                    message: 'Product not selected',
                    description: 'Product not find in the selected products',
                    className: notifClass,
                });
            }
        } else if( col == "Battery + Roof"){
            const product = (opportunityDetails.OpportunityProduct.filter((ele: any) => ((ele.Product_Type_Name === "Roof" || ele.Product_Type_Name ===  "Battery") && ele.ActiveFlag)));

            if (product.length > 1) {
                let Finance_Partner = product[0].Finance_Partner_Display_Name;
                if (Finance_Partner === 'Good Leap') Finance_Partner = 'GoodLeap';
                if (normalizePartner(Finance_Partner) == row.partner && product[0].Finance_Partner_Display_Name == product[1].Finance_Partner_Display_Name) {
                    const priceCell = row.prices[col];
                    const data: selectedQuote = {
                        product: col,
                        products: []
                    }
                    product.forEach((ele: any) => {
                        const priceCell = row.prices[ele.Product_Type_Name];
                        let financeMethod = priceCell?.financeMethod;

                        if (ele.Product_Type_Name === "Battery" && priceCell?.financeMethod === "Loan") {
                            financeMethod = "Battery Loan"
                        }
                        data.products!.push({
                            product: ele.Product_Type_Name,
                            partner: ele.Finance_Partner_Display_Name,
                            methord: financeMethod || '',
                            price: priceCell?.purchasePrice || null,
                            ExcelOffer: priceCell?.ExcelOffer || null,
                            payload: priceCell?.payload || null,
                            term: priceCell?.term,
                            apr: priceCell?.apr,
                        })
                    })
                    setConfirmQuoteCellKey(data);
                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Mismatched',
                        description: 'Finance partner does not match with selected product',
                        className: notifClass,
                    });
                }
            } else {
                noticeApi.warning({
                    message: 'Product not selected',
                    description: 'Product not find in the selected products',
                    className: notifClass,
                });
            }
        }
    };

    const updateQuoteVersion = () => {
        setConfirmQuoteCellKey(null);
        setQuotesRefreshTrigger(Date.now());
        setCommerceHubQuotesOpen(true);
    }

    const obdTheme = theme === 'dark' ? 'dark' : 'light';

    return (
        <div className={`obd-financing-grid-root obd-theme-${obdTheme}`}>
            {noticeContextHolder}

            <div className={`obd-card obd-card--financing${cardMod}`}>
                {!embeddedInPricingModal && (
                    <div
                        role="button"
                        tabIndex={0}
                        className="obd-quotes__header"
                        onClick={() => setSectionOpen((o) => !o)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSectionOpen((o) => !o); } }}
                        style={{ width: '100%' }}
                    >
                        <span className="obd-card__label obd-card__label--section-icon">
                            <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                                <CurrencyCircleDollar size={16} weight="duotone" />
                            </span>
                            Financing Options for the Finalized Design
                        </span>
                        <div className="obd-financing__header-end">
                            {(combinedFinancingSummary.totalMonthly > 0 || loading) ? (
                                <FinancingDealHealthChip
                                    monthlyDisplay={combinedMonthlyDisplay}
                                    className="obd-financing-combined__deal-health--header"
                                />
                            ) : null}
                            <div className="obd-financing__header-actions">
                            {onRefreshRequested && !sectionOpen && (
                                <button
                                    type="button"
                                    className="obd-utility__edit-btn obd-financing__refresh-btn"
                                    title="Refresh pricing"
                                    aria-label="Refresh pricing"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRefreshRequested();
                                    }}
                                >
                                    <ArrowsClockwise size={16} weight="regular" />
                                </button>
                            )}
                            <CaretDown size={14} weight="bold" className={`obd-quotes__chevron ${sectionOpen ? 'obd-quotes__chevron--open' : ''}`} />
                            </div>
                        </div>
                    </div>
                )}
                {contentOpen && (
                    <div className={`obd-financing__wrap${wrapMod}`}>
                        {embeddedInPricingModal ? (
                            <div className="obd-financing__modal-summary">
                                {systemHeaderBlock}
                                {toolbarBlock}
                            </div>
                        ) : (
                            <>
                                <div className="obd-financing__summary-row">
                                    {systemHeaderBlock}
                                    {toolbarBlock}
                                </div>
                                {loadError && (
                                    <div className="obd-financing__empty obd-financing__empty--compact obd-financing__api-error-banner" role="alert">
                                        <span className="obd-financing__load-error-text">{loadError}</span>
                                    </div>
                                )}
                                {!loadError && financingGrid.length === 0 && (
                                    <div className="obd-financing__empty" style={{ marginBottom: 8, fontSize: 12, color: 'rgba(148,163,184,0.9)' }}>
                                        Click the $ button on a design to load financing options.
                                    </div>
                                )}
                            </>
                        )}
                        {embeddedInPricingModal && loadError && (
                            <div className="obd-financing__empty obd-financing__empty--compact obd-financing__api-error-banner" role="alert">
                                <span className="obd-financing__load-error-text">{loadError}</span>
                            </div>
                        )}
                        {embeddedInPricingModal && !loadError && financingGrid.length === 0 && (
                            <div className="obd-financing__empty" style={{ marginBottom: 8, fontSize: 12, color: 'rgba(148,163,184,0.9)' }}>
                                Click the $ button on a design to load financing options.
                            </div>
                        )}
                        <div
                            className="obd-financing__view-tabs"
                            role="tablist"
                            aria-label="Financing view"
                        >
                            <button
                                type="button"
                                role="tab"
                                id="obd-financing-view-tab-combined"
                                aria-selected={financingView === 'combined'}
                                aria-controls="obd-financing-view-panel-combined"
                                className={`obd-financing__view-tab${financingView === 'combined' ? ' obd-financing__view-tab--active' : ''}`}
                                onClick={() => setFinancingView('combined')}
                            >
                                Combined financing
                            </button>
                            <button
                                type="button"
                                role="tab"
                                id="obd-financing-view-tab-grid"
                                aria-selected={financingView === 'grid'}
                                aria-controls="obd-financing-view-panel-grid"
                                className={`obd-financing__view-tab${financingView === 'grid' ? ' obd-financing__view-tab--active' : ''}`}
                                onClick={() => setFinancingView('grid')}
                            >
                                Best pricing options
                            </button>
                        </div>
                        {financingView === 'combined' ? (
                            <div
                                id="obd-financing-view-panel-combined"
                                role="tabpanel"
                                aria-labelledby="obd-financing-view-tab-combined"
                            >
                                <FinancingCombinedView
                                    summary={combinedFinancingSummary}
                                    loading={loading}
                                />
                            </div>
                        ) : (
                        <div
                            id="obd-financing-view-panel-grid"
                            role="tabpanel"
                            aria-labelledby="obd-financing-view-tab-grid"
                            className="obd-financing__table-container"
                        >
                            <table className="obd-financing__table">
                            <thead>
                                <tr>
                                    <th className="obd-financing__corner" scope="col">PARTNER</th>
                                    {/* {showTreeCutCostColumn && (
                                        <th className="obd-financing__col-header obd-financing__col-header--num" scope="col">
                                            TREE
                                        </th>
                                    )} */}
                                    {FINANCING_PRODUCTS.map((col) => (
                                        <th key={col} data-product={col} className="obd-financing__col-header" scope="col">
                                            {col.toUpperCase()}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayGridRows.map((row) => {
                                    const isCash = row.partner === 'Cash';
                                    return (
                                        <tr key={row.partner} className={isCash ? 'obd-financing__row--cash' : ''}>
                                            <td className="obd-financing__partner">{partnerLabelForGrid(row.partner)}</td>
                                            {FINANCING_PRODUCTS.map((col) => {
                                                const cell = row.prices[col];
                                                const cellKey = `${row.partner}::${col}`;
                                                const isSelected = selectedFinanceCells.has(cellKey);
                                                return (
                                                    <td
                                                        key={col}
                                                        data-product={col}
                                                        title={
                                                            cell && !isGenerateQuoteFromGridDisabled
                                                                ? 'Click to generate quote.'
                                                                : undefined
                                                        }
                                                        className={`obd-financing__cell ${cell ? 'obd-financing__cell--available' : ''} ${isSelected ? 'obd-financing__cell--selected' : ''}${cell && isGenerateQuoteFromGridDisabled ? ' obd-financing__cell--quote-disabled' : ''}`}
                                                        onClick={(e) => {
                                                            if (e.ctrlKey || e.metaKey) {
                                                                if (cell) toggleFinanceCell(cellKey);
                                                                return;
                                                            }
                                                            if (isGenerateQuoteFromGridDisabled) return;
                                                            if (cell) openGenerateQuoteModel(row, col);
                                                        }}
                                                    >
                                                        <span className="obd-financing__cell-inner">
                                                            {cell ? (
                                                                <>
                                                                    <span className="obd-financing__cell-content">
                                                                        {(!isCash && cell.recommended) ||
                                                                            (row.partner === 'GoodLeap' &&
                                                                                cell.recommended &&
                                                                                cell.purchasePrice != null &&
                                                                                cell.purchasePrice > 0) ? (
                                                                            <span className="obd-financing__cell-stack obd-financing__cell-stack--meta">
                                                                                {!isCash && cell.recommended && (
                                                                                    <span
                                                                                        className="obd-financing__recommended"
                                                                                        role="img"
                                                                                        aria-label="Recommended"
                                                                                        title="Recommended"
                                                                                    >
                                                                                        <Star size={15} weight="fill" className="obd-financing__recommended-icon" aria-hidden />
                                                                                    </span>
                                                                                )}
                                                                                {row.partner === 'GoodLeap' &&
                                                                                    cell.recommended &&
                                                                                    cell.purchasePrice != null &&
                                                                                    cell.purchasePrice > 0 && (
                                                                                        <span className="obd-financing__purchase">
                                                                                            Purchase{' '}
                                                                                            <span className="obd-financing__purchase-amt">
                                                                                                $
                                                                                                {cell.purchasePrice.toLocaleString(undefined, {
                                                                                                    maximumFractionDigits: 0,
                                                                                                })}
                                                                                            </span>
                                                                                        </span>
                                                                                    )}
                                                                            </span>
                                                                        ) : null}
                                                                        <span className="obd-financing__cell-stack obd-financing__cell-stack--payment">
                                                                            {!isCash && cell.financeMethod && (
                                                                                (cell.financeMethod.toLowerCase().includes('prod') ||
                                                                                    cell.financeMethod.toLowerCase().includes('guarantee'))
                                                                                    ? (
                                                                                        <Tooltip title={TOOLTIP_PROD_GUARANTEE}>
                                                                                            <span className="obd-financing__method">{cell.financeMethod}</span>
                                                                                        </Tooltip>
                                                                                    )
                                                                                    : <span className="obd-financing__method">{cell.financeMethod}</span>
                                                                            )}
                                                                            <span className="obd-financing__price">
                                                                                <span className="obd-financing__price-amount">
                                                                                    <span className="obd-financing__dollar">$</span>
                                                                                    {cell.monthly.toLocaleString()}
                                                                                </span>
                                                                                {isCash ? (
                                                                                    <span className="obd-financing__meta obd-financing__meta--period">one-time</span>
                                                                                ) : (
                                                                                    <span className="obd-financing__meta obd-financing__meta--period">/mo</span>
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                        {!isCash && (cell.apr > 0 || cell.term > 0 || cell.escalator != null) && (
                                                                            <span className="obd-financing__cell-stack obd-financing__cell-stack--terms">
                                                                                <span className="obd-financing__details">
                                                                                    {cell.apr != null && cell.apr > 0 ? (
                                                                                        <span className="obd-financing__apr">{cell.apr}% APR</span>
                                                                                    ) : cell.escalator != null ? (
                                                                                        <Tooltip title={TOOLTIP_ESCALATOR}>
                                                                                            <span className="obd-financing__apr obd-financing__apr--with-tooltip">{cell.escalator}% escalator</span>
                                                                                        </Tooltip>
                                                                                    ) : null}
                                                                                    {cell.term > 0 && <span className="obd-financing__term">{cell.term} yr</span>}
                                                                                </span>
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    {isSelected && <Check size={14} weight="bold" className="obd-financing__cell-check" aria-hidden />}
                                                                </>
                                                            ) : (
                                                                <span className="obd-financing__na" title="Not available">
                                                                    {FINANCING_CELL_NA_LABEL}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                )}
                {allPricingRaw && (
                    <DashboardModal
                        open={viewAllOpen}
                        onClose={() => setViewAllOpen(false)}
                        title="All pricing options"
                        width="min(96vw, 1320px)"
                        theme={theme}
                        className="obd-pricing-viewall-modal"
                        rootClassName={`obd-theme-${theme} obd-pricing-viewall-root--bento`}
                    >
                        <div className="obd-pricing-viewall">
                            <p className="obd-pricing-viewall__sub">Recommended options show a star in the status column. Select a layout above to improve pricing availability.</p>
                            <div className="obd-pricing-viewall__metrics" style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--theme-text-sec, #888)' }}>
                                <span><strong>System size:</strong> {systemSizeDisplay}</span>
                                <span><strong>Production:</strong> {productionDisplay.value}</span>
                                <span><strong>Multiplier:</strong> {multiplierDisplay}</span>
                            </div>
                            <div className="obd-pricing-viewall__table-wrap">
                                <table className="obd-pricing-viewall__table">
                                    <thead>
                                        <tr>
                                            <th className="obd-pricing-viewall__th">Tree details</th>
                                            <th className="obd-pricing-viewall__th">Partner</th>
                                            <th className="obd-pricing-viewall__th">Product</th>
                                            <th className="obd-pricing-viewall__th">Finance method</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Monthly</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Loan / Total</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Term</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">APR / Escalator</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Minimum Rate</th>
                                            <th className="obd-pricing-viewall__th obd-pricing-viewall__th--num">Rate Cap</th>
                                            <th className="obd-pricing-viewall__th">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewAllRows.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className={`obd-pricing-viewall__tr ${row.recommended ? 'obd-pricing-viewall__tr--rec' : ''} ${row.error ? 'obd-pricing-viewall__tr--err' : ''}`}
                                            >
                                                <td className="obd-pricing-viewall__td">
                                                    {row.treeDetailsText ? row.treeDetailsText : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td">{partnerLabelForGrid(row.partner)}</td>
                                                <td className="obd-pricing-viewall__td">{row.product}</td>
                                                <td className="obd-pricing-viewall__td">{row.financeMethod}</td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.error ? '—' : row.partner === 'Cash' ? '—' : (row.monthly != null && row.monthly > 0 ? `$${row.monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo` : '—')}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.loanAmount != null ? `$${row.loanAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.term != null ? `${row.term} yr` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.apr != null ? `${row.apr}% APR` : row.escalatorPct != null ? `${row.escalatorPct}% escalator` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.minimumRate != null ? `$${Number(row.minimumRate).toFixed(3)}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td obd-pricing-viewall__td--num">
                                                    {row.rateCap != null ? `$${Number(row.rateCap).toFixed(3)}` : '—'}
                                                </td>
                                                <td className="obd-pricing-viewall__td">
                                                    {row.error ? (
                                                        <span className="obd-pricing-viewall__err">{row.error}</span>
                                                    ) : row.recommended ? (
                                                        <span
                                                            className="obd-pricing-viewall__rec-star"
                                                            role="img"
                                                            aria-label="Recommended"
                                                            title="Recommended"
                                                        >
                                                            <Star size={16} weight="fill" className="obd-pricing-viewall__rec-star-icon" aria-hidden />
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </DashboardModal>
                )}

                <GenerateQuoteModal
                    confirmQuoteCellKey={confirmQuoteCellKey}
                    quoteForm={quoteForm}
                    priceQuote={priceQuote}
                    setConfirmQuoteCellKey={setConfirmQuoteCellKey}
                    setQuoteForm={setQuoteForm}
                    updateQuoteVersion={updateQuoteVersion}
                    noticeApi={noticeApi}
                    rcomendedQuote={true}
                />
            </div>
        </div>
    );
};

export default FinancingGridCard;

